import React from "react";
import PropTypes from "prop-types";
import {
    APPLICANT_TYPES,
    GENDER_TYPES,
    MARITIAL_STATUS_TYPES,
    NATURE_OF_ASSOCIATION_TYPES,
    PROFESSION_TYPES
} from "constants/enums";
import { Card, Field, Icon, ICONS, Pill, Toggle } from "./ui";
import {
    enumOptions,
    isoDate,
    namesMatch,
    datesMatch,
    isGuarantorType
} from "./data-utils";

/**
 * One applicant, in view or edit mode.
 *
 * View mode keeps the accent bars that make a KYC mismatch obvious. Edit mode
 * turns every field into an input over a local draft — including the ones the
 * narrow associate endpoint cannot take (marital status, profession,
 * shareholding, annual income, GST / partnership-deed names, alternate and
 * office phone) — and saves the whole applicant through
 * `company/case/<id>/associate-and-address-detail/`, the call Case Details ▸
 * Applicants makes.
 *
 * Addresses are edited in their own modal (shared AddAddressForm →
 * `address/create-update/`) rather than inline, because that is the only path
 * that can mint a city / district / state master row the operator types fresh;
 * the V2 endpoint takes those masters as plain integer ids.
 */

// ADDRESSES_TYPES: 6 = Permanent, 5 = Residence.
const ADDRESS_SLOTS = [
    { key: "permanent", title: "Permanent Address", addressType: 6 },
    { key: "residence", title: "Residence Address", addressType: 5 }
];

// Accents recompute off the draft, so a corrected name turns the bar green
// before the operator saves.
const checksFor = fields => {
    const name = [fields.first_name, fields.last_name].filter(Boolean).join(" ");
    return {
        name_pan: namesMatch(fields.name_pan, name),
        dob_pan: datesMatch(fields.dob_pan, fields.dob),
        name_aadhar: namesMatch(fields.name_aadhar, name),
        dob_aadhar: datesMatch(fields.dob_aadhar, fields.dob),
        name_gst: namesMatch(fields.name_gst, name)
    };
};

const accentFor = (ok, hasValue) => (hasValue ? (ok ? "ok" : "warn") : undefined);

const AddressBlock = ({ address }) => {
    if (!address) {
        return (
            <div className={"ops-ws__note"}>
                No address captured against this applicant yet — use Add to create one.
            </div>
        );
    }
    return (
        <div className={"ops-ws__field-grid"}>
            <Field label={"Address Line 1"} value={address.address_line_1} span />
            <Field label={"Address Line 2"} value={address.address_line_2} />
            <Field label={"Pincode"} value={address.pincode} />
            <Field label={"City"} value={address.city && address.city.name} />
            <Field label={"District"} value={address.district && address.district.name} />
            <Field label={"State"} value={address.state && address.state.name} />
            <Field label={"Ownership"} value={address.ownership} />
            <Field
                label={"Residing Since"}
                type={"date"}
                value={address.residing_since ? isoDate(address.residing_since) : ""}
            />
        </div>
    );
};

AddressBlock.propTypes = { address: PropTypes.object };

const Group = ({ title, extra, children }) => (
    <div className={"ops-ws__app-group"}>
        <div className={"ops-ws__app-group-head"}>
            <div className={"ops-ws__eyebrow ops-ws__eyebrow--primary"}>{title}</div>
            {extra || null}
        </div>
        {children}
    </div>
);

Group.propTypes = {
    title: PropTypes.string,
    extra: PropTypes.node,
    children: PropTypes.node
};

class ApplicantCard extends React.Component {
    state = { editing: false, draft: null, roleError: null };

    startEdit = () => {
        const { applicant } = this.props;
        this.setState({
            editing: true,
            draft: {
                fields: Object.assign({}, applicant.fields),
                type: applicant.type,
                is_main_borrower: applicant.isMainBorrower,
                is_key_contact: applicant.isKeyContact
            }
        });
    };

    cancelEdit = () => this.setState({ editing: false, draft: null, roleError: null });

    /**
     * A guarantor stands behind the borrowing rather than doing it, so the
     * two roles are mutually exclusive — and a case made up of nothing but
     * guarantors has nobody borrowing. Both are refused here, at the control,
     * rather than only on save.
     */
    setType = value => {
        const type = Number(value);
        if (isGuarantorType(type)) {
            const others = this.props.otherTypes || [];
            if (others.length && others.every(isGuarantorType)) {
                this.setState({
                    roleError:
                        "At least one applicant has to be someone other than a guarantor."
                });
                return;
            }
        }
        this.setState(prev => ({
            roleError: null,
            draft: Object.assign({}, prev.draft, {
                type,
                // Switching to guarantor gives up the main-borrower role.
                is_main_borrower: isGuarantorType(type) ? false : prev.draft.is_main_borrower
            })
        }));
    };

    setField = (key, value) =>
        this.setState(prev => ({
            draft: Object.assign({}, prev.draft, {
                fields: Object.assign({}, prev.draft.fields, { [key]: value })
            })
        }));

    setTop = (key, value) =>
        this.setState(prev => ({
            draft: Object.assign({}, prev.draft, { [key]: value })
        }));

    save = () => {
        this.props.onSave(this.props.applicant, this.state.draft, saved => {
            if (saved) this.setState({ editing: false, draft: null, roleError: null });
        });
    };

    render() {
        const { applicant, index, canEdit, busy } = this.props;
        const { editing, draft } = this.state;
        const f = editing ? draft.fields : applicant.fields;
        const type = editing ? draft.type : applicant.type;
        const mainBorrower = editing ? draft.is_main_borrower : applicant.isMainBorrower;
        const keyContact = editing ? draft.is_key_contact : applicant.isKeyContact;
        const permanent = applicant.permanent;
        const residence = applicant.residence;
        const sameAddress = applicant.residenceMirrorsPermanent;
        const checks = editing ? checksFor(f) : applicant.checks;
        const isGuarantor = isGuarantorType(type);
        const alt = index % 2 === 1;
        // A field is only writable in edit mode; passing no onChange renders it
        // read-only (see ui.js), which is exactly the view-mode presentation.
        const edit = key => (editing ? value => this.setField(key, value) : undefined);
        const editNum = key =>
            editing ? value => this.setField(key, value ? Number(value) : null) : undefined;

        return (
            <Card>
                <div className={"ops-ws__app-head" + (alt ? " ops-ws__app-head--alt" : "")}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        <div className={"ops-ws__avatar" + (alt ? " ops-ws__avatar--alt" : "")}>
                            {applicant.initials}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span className={"ops-ws__app-name"}>{applicant.name}</span>
                                <Pill tone={"info"}>CIBIL {applicant.cibil || "—"}</Pill>
                            </div>
                            <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap", minHeight: 19 }}>
                                {mainBorrower ? <Pill tone={"info"}>Main Borrower</Pill> : null}
                                {keyContact ? <Pill tone={"info"}>Authorised Signatory</Pill> : null}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 11, alignItems: "flex-end", flex: "none" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <select
                                className={"ops-ws__select"}
                                style={{ width: 170, borderLeftWidth: 1 }}
                                value={type || ""}
                                disabled={!editing}
                                onChange={e => this.setType(e.target.value)}
                            >
                                {enumOptions(APPLICANT_TYPES).map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {editing ? (
                                <React.Fragment>
                                    <button
                                        type={"button"}
                                        className={"ops-ws__btn"}
                                        disabled={busy}
                                        onClick={this.cancelEdit}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type={"button"}
                                        className={"ops-ws__btn ops-ws__btn--primary"}
                                        disabled={busy}
                                        onClick={this.save}
                                    >
                                        {busy ? "Saving…" : "Save applicant"}
                                    </button>
                                </React.Fragment>
                            ) : (
                                <button
                                    type={"button"}
                                    className={"ops-ws__btn"}
                                    disabled={!canEdit}
                                    onClick={this.startEdit}
                                    title={
                                        canEdit
                                            ? undefined
                                            : "Needs documents_edit and the case task assigned to you"
                                    }
                                >
                                    <Icon d={ICONS.pen} size={13} />
                                    Edit
                                </button>
                            )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                            <Toggle
                                label={"Main Borrower"}
                                on={isGuarantor ? false : mainBorrower}
                                disabled={!editing || isGuarantor}
                                onChange={next => this.setTop("is_main_borrower", next)}
                            />
                            <Toggle
                                label={"Authorised Signatory"}
                                on={keyContact}
                                disabled={!editing}
                                onChange={next => this.setTop("is_key_contact", next)}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ padding: "4px 18px 18px" }}>
                    {this.state.roleError ? (
                        <div
                            className={"ops-ws__note"}
                            style={{ marginBottom: 14, display: "flex", gap: 9 }}
                        >
                            <Icon d={ICONS.alert} size={15} stroke={"#b54708"} />
                            <span>{this.state.roleError}</span>
                        </div>
                    ) : null}
                    {isGuarantor ? (
                        <div className={"ops-ws__note"} style={{ marginBottom: 14 }}>
                            {"A guarantor cannot be the main borrower on the case."}
                        </div>
                    ) : null}
                    <Group title={"Identity & personal"}>
                        <div className={"ops-ws__field-grid"}>
                            <Field label={"First & Middle Name"} value={f.first_name} onChange={edit("first_name")} />
                            <Field label={"Last Name"} value={f.last_name} onChange={edit("last_name")} />
                            <Field
                                label={"Gender"}
                                required
                                value={f.gender || ""}
                                options={enumOptions(GENDER_TYPES)}
                                onChange={editNum("gender")}
                            />
                            <Field
                                label={"Date of Birth"}
                                type={"date"}
                                value={isoDate(f.dob)}
                                onChange={edit("dob")}
                            />
                            <Field
                                label={"Marital Status"}
                                value={f.marital_status || ""}
                                options={enumOptions(MARITIAL_STATUS_TYPES)}
                                onChange={editNum("marital_status")}
                            />
                            <Field
                                label={"Title"}
                                value={f.nature_of_association || ""}
                                options={enumOptions(NATURE_OF_ASSOCIATION_TYPES)}
                                onChange={editNum("nature_of_association")}
                            />
                            <Field label={"Father Name"} value={f.father_name} onChange={edit("father_name")} />
                            <Field label={"Spouse Name"} value={f.spouse_name} onChange={edit("spouse_name")} />
                            <Field label={"Mother Maiden Name"} value={f.mother_name} onChange={edit("mother_name")} />
                            <Field
                                label={"Profession"}
                                value={f.profession || ""}
                                options={enumOptions(PROFESSION_TYPES)}
                                onChange={editNum("profession")}
                            />
                        </div>
                    </Group>

                    <Group title={"Contact"}>
                        <div className={"ops-ws__field-grid"}>
                            <Field label={"Phone Number"} value={f.phone} onChange={edit("phone")} />
                            <Field label={"Alternate Phone Number"} value={f.phone_residence} onChange={edit("phone_residence")} />
                            <Field label={"Office Phone / Landline"} value={f.phone_office} onChange={edit("phone_office")} />
                            <Field label={"Email"} value={f.email} onChange={edit("email")} />
                        </div>
                    </Group>

                    <Group title={"IDs & KYC"}>
                        <div className={"ops-ws__field-grid"}>
                            <Field label={"PAN"} required value={f.pan} onChange={edit("pan")} />
                            <Field label={"Aadhaar"} value={f.aadhar} onChange={edit("aadhar")} />
                            <Field
                                label={"Name as on PAN"}
                                value={f.name_pan}
                                accent={accentFor(checks.name_pan, !!f.name_pan)}
                                onChange={edit("name_pan")}
                            />
                            <Field
                                label={"Date of Birth as on PAN"}
                                type={"date"}
                                value={isoDate(f.dob_pan)}
                                accent={accentFor(checks.dob_pan, !!f.dob_pan)}
                                onChange={edit("dob_pan")}
                            />
                            <Field
                                label={"Name as on Aadhaar"}
                                value={f.name_aadhar}
                                accent={accentFor(checks.name_aadhar, !!f.name_aadhar)}
                                onChange={edit("name_aadhar")}
                            />
                            <Field
                                label={"Date of Birth as on Aadhaar"}
                                type={"date"}
                                value={isoDate(f.dob_aadhar)}
                                accent={accentFor(checks.dob_aadhar, !!f.dob_aadhar)}
                                onChange={edit("dob_aadhar")}
                            />
                            <Field
                                label={"Name as on GST"}
                                value={f.name_gst}
                                accent={accentFor(checks.name_gst, !!f.name_gst)}
                                onChange={edit("name_gst")}
                            />
                            <Field label={"Name as on Partnership Deed"} value={f.name_pdeed} onChange={edit("name_pdeed")} />
                        </div>
                    </Group>

                    <Group title={"Financials"}>
                        <div className={"ops-ws__field-grid"}>
                            <Field label={"Annual Income"} value={f.annual_income} onChange={edit("annual_income")} />
                            <Field label={"Shareholding (%)"} value={f.shareholding} onChange={edit("shareholding")} />
                        </div>
                    </Group>

                    {ADDRESS_SLOTS.map(slot => {
                        const isResidence = slot.key === "residence";
                        const address = isResidence ? residence : permanent;
                        return (
                            <Group
                                key={slot.key}
                                title={slot.title}
                                extra={
                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                        {isResidence && sameAddress ? (
                                            <Pill tone={"neutral"}>Same as permanent</Pill>
                                        ) : null}
                                        <button
                                            type={"button"}
                                            className={"ops-ws__link"}
                                            disabled={!canEdit || busy}
                                            onClick={() =>
                                                this.props.onEditAddress(
                                                    applicant,
                                                    slot.addressType,
                                                    address
                                                )
                                            }
                                        >
                                            <Icon d={ICONS.pen} size={13} />
                                            {address ? "Edit" : "Add"}
                                        </button>
                                    </div>
                                }
                            >
                                <AddressBlock address={address} />
                            </Group>
                        );
                    })}

                </div>
            </Card>
        );
    }
}

ApplicantCard.propTypes = {
    applicant: PropTypes.object.isRequired,
    // The other applicants' types, so this card can refuse a change that
    // would leave the case with nothing but guarantors.
    otherTypes: PropTypes.array,
    index: PropTypes.number,
    canEdit: PropTypes.bool,
    busy: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onEditAddress: PropTypes.func.isRequired
};

export default ApplicantCard;
